import { useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { useCallback, useMemo } from 'react';
import {
  FieldClass,
  IColumnWithTypeInput,
  IDataModelPreviewColumnsDataQuery,
  IDataModelPreviewColumnsDataQueryVariables,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useLazyQuery } from '@apollo/client';
import { DATA_MODEL_PREVIEW_COLUMNS_DATA } from '../../../../utils/apollo/dataModeler/queries';
import { assertNotNil } from '../../../../utils/types';
import {
  convertPreviewColumnDataToRows,
  getViewModelFields,
  IPreviewDataRows,
  listToHashMapFromKey,
} from '../utils';
import { typeParamsToTypeParamsInput } from '../../../../redux/slices/dataModeler/utils';
import { IDerivedColumnDraft } from '../../../../redux/slices/dataModeler';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  viewKey: string;
  tableKey: string;
  draftColumn: IDerivedColumnDraft;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
  cachedData: IPreviewDataRows | null;
  setCachedData: (newData: IPreviewDataRows) => void;
}

const PREVIEW_DRAFT_COLUMNS_DATA_DISPLAY_LIMIT = 5;

const useDerivedColumnPreviewDataModel = ({
  viewKey,
  tableKey,
  draftColumn,
  availableColumnsFromServer,
  cachedData,
  setCachedData,
}: IProps) => {
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);

  const [previewColumnsQuery, { loading }] = useLazyQuery<
    IDataModelPreviewColumnsDataQuery,
    IDataModelPreviewColumnsDataQueryVariables
  >(DATA_MODEL_PREVIEW_COLUMNS_DATA);

  const definitionInput = useMemo(() => {
    switch (draftColumn.definitionType) {
      case 'MappedString':
        return {
          mappedString: draftColumn.definition?.mappedString ?? null,
          rawSql: null,
        };
      case 'RawSql':
        return {
          rawSql: draftColumn.definition?.rawSql ?? null,
          mappedString: null,
        };
      case null:
        return null;
      default:
        throw new UnreachableCaseError(draftColumn.definitionType);
    }
  }, [
    draftColumn.definition?.mappedString,
    draftColumn.definition?.rawSql,
    draftColumn.definitionType,
  ]);

  const handlePreviewClick = useCallback(async () => {
    const isExistingTable =
      schemaObj.fact.tableKeys.includes(tableKey) ||
      !!schemaObj.dimensions.find((dim) => dim.tableKeys.includes(tableKey));
    const addedTable = isExistingTable
      ? null
      : draftEdits?.addedFactsData?.find(
          (fact) => fact.tableKey === tableKey,
        ) ??
        draftEdits?.addedDimensionsData?.find(
          (dim) => dim.tableKey === tableKey,
        );

    // Our draft field Input
    const columnWithType: IColumnWithTypeInput = {
      column: draftColumn.columnName,
      typeInput: {
        type: draftColumn.fieldType,
        typeParams: {
          fixedTz: draftColumn.typeParams.fixedTz,
          arrayItemType: draftColumn.typeParams.arrayItemType,
          customDefinition: definitionInput,
          linearDistribution: draftColumn.typeParams.linearDistribution,
          structDefinition: draftColumn.typeParams.structDefinition
            ? {
                fields: draftColumn.typeParams.structDefinition.fields.map(
                  (fld) => {
                    return {
                      type: fld.type,
                      name: fld.name,
                    };
                  },
                ),
              }
            : null,
          mapDefinition: draftColumn.typeParams.mapDefinition
            ? {
                keyType: draftColumn.typeParams.mapDefinition.keyType,
                valueType: draftColumn.typeParams.mapDefinition.valueType,
              }
            : null,
        },
        fieldClass: FieldClass.Basic,
      },
    };

    // All draft mapped columns from this table, these can have a type override
    const draftColumns: IColumnWithTypeInput[] =
      draftEdits?.draftFieldsData?.flatMap((draftField) => {
        if (draftField.tableKey === tableKey) {
          if (!!draftField.addInput) {
            return [
              {
                column: draftField.column.column,
                typeInput: draftField.addInput.typeInput,
              },
            ];
          }
          if (!!draftField.extendInput) {
            return [
              {
                column: draftField.column.column,
                typeInput: draftField.extendInput.typeInput,
              },
            ];
          }
          return [];
        } else {
          return [];
        }
      }) ?? [];

    // Now convert all not already mapped columns coming from the server to IColumnWithTypeInput -
    // draftColumns would override these. Also keep track of foundDraftColumns, so we
    // can filter them out in the final list and avoid duplicates
    const alreadyMappedTableColumns = getViewModelFields(
      schemaObj,
      viewKey,
    ).flatMap((viewField) => {
      const tableField = viewField.tableFields.find(
        (tf) => tf.tableKey === tableKey,
      );
      return tableField ? [tableField.column] : [];
    });
    const alreadyMappedTableColumnsSet = new Set(alreadyMappedTableColumns);
    const draftColumnsByColumn = listToHashMapFromKey(draftColumns, 'column');
    const foundDraftColumns = new Set<string>();
    const allUnmappedColumnsFromServer: IColumnWithTypeInput[] =
      availableColumnsFromServer
        .filter(
          (tableColumn) =>
            !alreadyMappedTableColumnsSet.has(tableColumn.column),
        )
        .map((tableColumn) => {
          const override = draftColumnsByColumn[tableColumn.column];
          if (override) {
            foundDraftColumns.add(override.column);
            return override;
          }
          return {
            column: tableColumn.column,
            typeInput: {
              type: tableColumn.type,
              typeParams: typeParamsToTypeParamsInput(tableColumn.typeParams),
              fieldClass: FieldClass.Basic,
            },
          };
        });

    const allUnmappedColumns = [
      ...allUnmappedColumnsFromServer,
      ...draftColumns.filter(({ column }) => !foundDraftColumns.has(column)),
    ];

    const response = await previewColumnsQuery({
      variables: {
        schemaKey: draftEdits?.isNew ? null : schemaObj?.key ?? null,
        tableKey: isExistingTable ? tableKey : null,
        connection: draftEdits?.dataSource?.connection ?? null,
        sqlTableName: addedTable?.sqlTableName ?? null,
        draftColumns: allUnmappedColumns,
        selectColumns: [columnWithType],
        expandReferences: true,
        // XXX requesting much more than what is needed from Kobol and hoping to get some unique values
        limit: PREVIEW_DRAFT_COLUMNS_DATA_DISPLAY_LIMIT * 100,
      },
    });
    if (!response.data) {
      setCachedData({
        header: [],
        rows: [],
        sqlQuery: null,
        errors: ['Unexpected server error', response.error?.message ?? ''],
      });
      return;
    }
    const { columns, errors, sqlQuery } =
      response.data.dataModelPreviewColumnsData;
    setCachedData(
      convertPreviewColumnDataToRows(
        columns,
        errors,
        sqlQuery,
        PREVIEW_DRAFT_COLUMNS_DATA_DISPLAY_LIMIT,
      ),
    );
  }, [
    availableColumnsFromServer,
    definitionInput,
    draftColumn,
    draftEdits,
    previewColumnsQuery,
    schemaObj,
    viewKey,
    tableKey,
    setCachedData,
  ]);
  return {
    cachedData,
    handlePreviewClick,
    loading,
  };
};

export default useDerivedColumnPreviewDataModel;
